/**
 * 習熟度ダッシュボード(⑨)の集計。
 *
 * 見せ方は商談デモ(ichininmae-web public/demo)を正とする。ただし
 * **デモに出ている「作業時間◯倍」「包丁の入れ直し◯回」は算出していない**ため出さない。
 * 実際に持っている値(評価ごとの総合スコアと時刻)だけで組み立てる。
 */

export interface EvaluationRow {
  id: string;
  dishId: string;
  dishName: string | null;
  traineeUid: string;
  score: number | null;
  status: "queued" | "running" | "succeeded" | "failed";
  createdAt: Date | null;
}

export interface Person {
  uid: string;
  displayName: string;
}

/** 行の候補になる利用者(役割は絞り込みには使わず、枠を先に出すためだけに見る) */
export interface CandidateUser extends Person {
  role: "admin" | "chef" | "supervisor" | "worker";
}

/**
 * 習熟度の行に出す人を決める(#124)。
 *
 * **役割では絞らない。** 実際に動画を上げて採点された人は、管理者でも料理長でも出す。
 * 「採点されるのは作業者だけ」という前提で role を条件にしていたため、料理長や管理者が
 * 自分で撮って上げた結果がどこにも出ず、画面が空のままになっていた。
 *
 * 作業者・上長は、まだ採点が無くても行を出す(これから埋まる枠として見せる)。
 * 利用者文書が消えていても採点履歴は残るため、その uid も取りこぼさず行にする。
 */
export function buildPeople(
  users: CandidateUser[],
  evaluations: Pick<EvaluationRow, "traineeUid">[],
): Person[] {
  const evaluated = new Set(evaluations.map((e) => e.traineeUid));
  const rows: Person[] = users
    .filter((u) => u.role === "worker" || u.role === "supervisor" || evaluated.has(u.uid))
    .map((u) => ({ uid: u.uid, displayName: u.displayName }));
  const known = new Set(rows.map((r) => r.uid));
  for (const uid of evaluated) {
    if (!known.has(uid)) rows.push({ uid, displayName: uid });
  }
  return rows;
}

export interface Dish {
  id: string;
  name: string;
  /** 中国語表示用の料理名(DishDoc.nameZh の写し) */
  nameZh?: string | null;
}

/** 一人前の基準(#75 / LP・マニュアルの記載と揃える) */
export const ICHININMAE_SCORE = 90;
export const ICHININMAE_STREAK = 2;

export interface Cell {
  dishId: string;
  /** 古い順。採点済み(succeeded かつ score あり)だけ */
  history: { id: string; score: number; at: Date | null }[];
  latest: number | null;
  /** 直前の採点との差。初回は null */
  delta: number | null;
  /** 最新の評価ID(レポートへ飛ぶ) */
  latestId: string | null;
  ichininmae: boolean;
}

export interface MatrixRow {
  person: Person;
  cells: Cell[];
}

function scored(rows: EvaluationRow[]): EvaluationRow[] {
  return rows.filter((r) => r.status === "succeeded" && typeof r.score === "number");
}

function byTime(a: EvaluationRow, b: EvaluationRow): number {
  return (a.createdAt?.getTime() ?? 0) - (b.createdAt?.getTime() ?? 0);
}

/**
 * 90点以上を続けて2回で一人前。
 * 「一度きりのまぐれを一人前と呼ばない」ため、直近が連続していることを見る。
 *
 * **朱印は「現在の状態」であって到達記録ではない。** 直近2回だけを見るので、
 * 90点を下回れば消える。これは意図した仕様（2026-08-16 柴田確認）。
 *
 * ⚠️ Cloudflare 版（micro-saas/apps/ichininmae-web/src/state/proficiency.ts）は
 * **逆の仕様**で、「一度立った朱印は下ろさない（到達の記録）」と書いてある。
 * どちらかをバグと見て直さないこと。**こちらが正。**
 */
export function isIchininmae(history: { score: number }[]): boolean {
  if (history.length < ICHININMAE_STREAK) return false;
  return history
    .slice(-ICHININMAE_STREAK)
    .every((h) => h.score >= ICHININMAE_SCORE);
}

export function buildMatrix(
  evaluations: EvaluationRow[],
  people: Person[],
  dishes: Dish[],
): MatrixRow[] {
  const ok = scored(evaluations);
  return people.map((person) => ({
    person,
    cells: dishes.map((dish) => {
      const history = ok
        .filter((r) => r.traineeUid === person.uid && r.dishId === dish.id)
        .sort(byTime)
        .map((r) => ({ id: r.id, score: r.score as number, at: r.createdAt }));
      const latest = history.length > 0 ? history[history.length - 1] : null;
      const prev = history.length > 1 ? history[history.length - 2] : null;
      return {
        dishId: dish.id,
        history,
        latest: latest?.score ?? null,
        delta: latest && prev ? latest.score - prev.score : null,
        latestId: latest?.id ?? null,
        ichininmae: isIchininmae(history),
      };
    }),
  }));
}

export interface Kpis {
  /** 採点済みセルの最新スコア平均。1件も無ければ null */
  average: number | null;
  ichininmaeCells: number;
  /** 採点実績のあるセル数(分母。総当たりではない) */
  scoredCells: number;
  /** 最新スコアが60点未満のセル */
  needsFocus: number;
  /** 当月の採点回数 */
  thisMonth: number;
}

export function buildKpis(matrix: MatrixRow[], evaluations: EvaluationRow[], now: Date): Kpis {
  const cells = matrix.flatMap((r) => r.cells).filter((c) => c.latest != null);
  const sum = cells.reduce((a, c) => a + (c.latest as number), 0);
  const month = `${now.getFullYear()}-${now.getMonth()}`;
  return {
    average: cells.length > 0 ? Math.round(sum / cells.length) : null,
    ichininmaeCells: cells.filter((c) => c.ichininmae).length,
    scoredCells: cells.length,
    needsFocus: cells.filter((c) => (c.latest as number) < 60).length,
    thisMonth: scored(evaluations).filter(
      (r) => r.createdAt && `${r.createdAt.getFullYear()}-${r.createdAt.getMonth()}` === month,
    ).length,
  };
}

export interface FocusItem {
  person: Person;
  dish: Dish;
  score: number;
  delta: number | null;
  evaluationId: string | null;
  level: "focus" | "watch";
}

/**
 * 要重点指導。60点未満を focus、60〜79 を watch とし、点の低い順に返す。
 * 区切りはデモの凡例(80以上=良好 / 60-79=継続指導 / 60未満=要重点指導)に合わせる。
 */
export function buildFocus(matrix: MatrixRow[], dishes: Dish[], limit = 6): FocusItem[] {
  const out: FocusItem[] = [];
  for (const row of matrix) {
    for (const cell of row.cells) {
      if (cell.latest == null || cell.latest >= 80) continue;
      const dish = dishes.find((d) => d.id === cell.dishId);
      if (!dish) continue;
      out.push({
        person: row.person,
        dish,
        score: cell.latest,
        delta: cell.delta,
        evaluationId: cell.latestId,
        level: cell.latest < 60 ? "focus" : "watch",
      });
    }
  }
  return out.sort((a, b) => a.score - b.score).slice(0, limit);
}

export interface TrendSeries {
  person: Person;
  points: { score: number; at: Date | null }[];
}

/** 指定した品目の、人ごとのスコア推移(古い順)。点が無い人は返さない */
export function buildTrend(matrix: MatrixRow[], dishId: string): TrendSeries[] {
  return matrix
    .map((row) => ({
      person: row.person,
      points: (row.cells.find((c) => c.dishId === dishId)?.history ?? []).map((h) => ({
        score: h.score,
        at: h.at,
      })),
    }))
    .filter((s) => s.points.length > 0);
}
